import React, { useState, useEffect } from 'react';
import { Paperclip, Trash2, Upload } from 'lucide-react';
import ReactSelect from 'react-select';
import { useCreateProject, useUpdateProject, useUsers, useUploadProjectFile, useDeleteProjectFile } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { FormField, FormGrid, Input, Select, Textarea } from './ui/Form';
import { Alert } from './ui/States';
import { fileUrl } from '../services/api';

const FORM_ID = 'project-form';

const VENDORS = ['AZENTIO', 'MODEFIN', 'NI', 'MYBANK ICT'];
const DEPARTMENTS = [
'OPERATIONS',
'Finance',
'Human Resource and Admin',
'Internal Audit',
'ICT',
'Credit/nvestment',
'Risk and Compliance',
'Business',
'Public Relations',
'Chief Executive Office'
];

/* react-select themed against the design tokens so it matches native controls. */
const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 'var(--control-h)',
        borderRadius: 'var(--radius-md)',
        borderColor: state.isFocused ? 'var(--accent)' : 'var(--border-strong)',
        boxShadow: state.isFocused ? 'var(--shadow-focus)' : 'none',
        fontSize: 'var(--fs-body)',
        '&:hover': { borderColor: state.isFocused ? 'var(--accent)' : 'var(--border-strong)' },
    }),
    valueContainer: (base) => ({ ...base, padding: '2px 8px' }),
    placeholder: (base) => ({ ...base, color: 'var(--text-muted)', fontSize: 'var(--fs-body)' }),
    input: (base) => ({ ...base, fontSize: 'var(--fs-body)', margin: 0, paddingTop: 0, paddingBottom: 0 }),
    indicatorSeparator: (base) => ({ ...base, backgroundColor: 'var(--border-color)' }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: 'var(--primary-subtle)',
        borderRadius: 'var(--radius-sm)',
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: 'var(--navy-600)',
        fontSize: 'var(--fs-micro)',
        fontWeight: 500,
        padding: '2px 4px',
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: 'var(--navy-500)',
        borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
        '&:hover': { backgroundColor: 'var(--navy-100)', color: 'var(--navy-700)' },
    }),
    menu: (base) => ({
        ...base,
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-color)',
        boxShadow: 'var(--shadow-dropdown)',
        fontSize: 'var(--fs-body)',
        zIndex: 95,
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
            ? 'var(--primary-subtle)'
            : state.isFocused
                ? 'var(--bg-subtle)'
                : 'transparent',
        color: 'var(--text-primary)',
        padding: '7px 10px',
    }),
};

const ProjectModal = ({ project, onClose }) => {
    const { data: userResult } = useUsers();
    const { user: currentUser } = useAuth();
    const users = userResult?.data || [];
    const createProject = useCreateProject();
    const updateProject = useUpdateProject();
    const uploadFile = useUploadProjectFile();
    const deleteFile = useDeleteProjectFile();
    const isEditMode = !!project;
    const adminRoles = ['Manager', 'Head of Department', 'Developer'];
    const isGlobalAdmin = adminRoles.includes(currentUser?.role);
    const canEditAllFields = !isEditMode || isGlobalAdmin;

    const [formData, setFormData] = useState({
        project_name: '',
        project_code: '',
        description: '',
        vendor: '',
        department: '',
        assignee_id: '',
        start_date: '',
        end_date: '',
        status: 'planned',
        priority: 'medium',
        budget: '',
        progress: 0,
        team_members: [],
    });

    const [selectedFile, setSelectedFile] = useState(null);

    useEffect(() => {
        if (project) {
            setFormData({
                project_name: project.project_name || '',
                project_code: project.project_code || '',
                description: project.description || '',
                vendor: project.vendor || '',
                department: project.department || '',
                assignee_id: project.assignee_id || '',
                start_date: project.start_date ? new Date(project.start_date).toISOString().split('T')[0] : '',
                end_date: project.end_date ? new Date(project.end_date).toISOString().split('T')[0] : '',
                status: project.status || 'planned',
                priority: project.priority || 'medium',
                budget: project.budget || '',
                progress: project.progress || 0,
                team_members: project.team_members ? project.team_members.map(m => m.user_id) : [],
            });
        }
    }, [project]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            let projectId = project?.id;
            if (isEditMode) {
                await updateProject.mutateAsync({ id: project.id, data: formData });
            } else {
                const result = await createProject.mutateAsync(formData);
                projectId = result.data.data.id;
            }

            // Handle file upload if any
            if (selectedFile && projectId) {
                const fileData = new FormData();
                fileData.append('file', selectedFile);
                await uploadFile.mutateAsync({ id: projectId, formData: fileData });
            }

            onClose();
        } catch (error) {
            console.error('Error saving project:', error);
        }
    };

    const handleFileChange = (e) => {
        setSelectedFile(e.target.files[0]);
    };

    const handleDeleteFile = async (fileId) => {
        if (window.confirm('Delete this attachment? This cannot be undone.')) {
            try {
                await deleteFile.mutateAsync({ id: project.id, fileId });
            } catch (error) {
                console.error('Error deleting file:', error);
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: name === 'progress' ? parseInt(value) : value,
        });
    };

    const handleTeamChange = (selectedOptions) => {
        setFormData({
            ...formData,
            team_members: selectedOptions ? selectedOptions.map(option => option.value) : [],
        });
    };

    const isLoading = createProject.isPending || updateProject.isPending || uploadFile.isPending;

    const userOptions = users.map(user => ({
        value: user.id,
        label: `${user.first_name} ${user.last_name}`
    }));

    return (
        <Modal
            isOpen
            onClose={onClose}
            size="xl"
            title={isEditMode ? 'Edit project' : 'New project'}
            subtitle={
                isEditMode
                    ? 'Update scope, ownership, timeline and attachments.'
                    : 'Set up the project, assign a manager and define the timeline.'
            }
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button type="submit" form={FORM_ID} variant="primary" loading={isLoading}>
                        {isEditMode ? 'Save changes' : 'Create project'}
                    </Button>
                </>
            }
        >
            {!canEditAllFields && (
                <Alert variant="info" className="mb-4">
                    Your role lets you update status and progress. The remaining fields are managed by
                    a project manager.
                </Alert>
            )}

            <form id={FORM_ID} onSubmit={handleSubmit} className="space-y-4">
                <FormGrid cols={3}>
                    <FormField label="Project name" required htmlFor="project-name" className="sm:col-span-2">
                        <Input
                            id="project-name"
                            name="project_name"
                            value={formData.project_name}
                            onChange={handleChange}
                            required
                            disabled={!canEditAllFields}
                            placeholder="e.g. Core banking upgrade"
                        />
                    </FormField>

                    <FormField label="Project code" htmlFor="project-code" hint="Generated automatically.">
                        <Input
                            id="project-code"
                            name="project_code"
                            value={formData.project_code}
                            onChange={handleChange}
                            placeholder="Auto-generated"
                            readOnly={!isEditMode}
                            disabled
                        />
                    </FormField>
                </FormGrid>

                <FormGrid cols={3}>
                    <FormField label="Vendor" htmlFor="project-vendor">
                        <Select
                            id="project-vendor"
                            name="vendor"
                            value={formData.vendor}
                            onChange={handleChange}
                            disabled={!canEditAllFields}
                        >
                            <option value="">Select vendor</option>
                            {VENDORS.map((v) => (
                                <option key={v} value={v}>
                                    {v}
                                </option>
                            ))}
                            <option value="OTHER">OTHER</option>
                        </Select>
                    </FormField>

                    <FormField label="Department" htmlFor="project-department">
                        <Select
                            id="project-department"
                            name="department"
                            value={formData.department}
                            onChange={handleChange}
                            disabled={!canEditAllFields}
                        >
                            <option value="">Select department</option>
                            {DEPARTMENTS.map((d) => (
                                <option key={d} value={d}>
                                    {d}
                                </option>
                            ))}
                        </Select>
                    </FormField>

                    <FormField label="Project manager" htmlFor="project-assignee">
                        <Select
                            id="project-assignee"
                            name="assignee_id"
                            value={formData.assignee_id}
                            onChange={handleChange}
                            disabled={!canEditAllFields}
                        >
                            <option value="">Select manager</option>
                            {users?.map((user) => (
                                <option key={user.id} value={user.id}>
                                    {user.first_name} {user.last_name}
                                </option>
                            ))}
                        </Select>
                    </FormField>
                </FormGrid>

                <FormField label="Team members" htmlFor="project-team">
                    <ReactSelect
                        inputId="project-team"
                        isMulti
                        name="team_members"
                        options={userOptions}
                        value={userOptions.filter((option) => formData.team_members.includes(option.value))}
                        onChange={handleTeamChange}
                        styles={selectStyles}
                        isDisabled={!canEditAllFields}
                        placeholder="Select team members…"
                    />
                </FormField>

                <FormField label="Description" htmlFor="project-description">
                    <Textarea
                        id="project-description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={3}
                        disabled={!canEditAllFields}
                        placeholder="Scope, objectives and any key constraints."
                    />
                </FormField>

                <FormGrid cols={3}>
                    <FormField label="Budget (USD)" htmlFor="project-budget">
                        <Input
                            id="project-budget"
                            type="number"
                            name="budget"
                            value={formData.budget}
                            onChange={handleChange}
                            disabled={!canEditAllFields}
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                        />
                    </FormField>

                    <FormField label="Priority" htmlFor="project-priority">
                        <Select
                            id="project-priority"
                            name="priority"
                            value={formData.priority}
                            onChange={handleChange}
                            disabled={!canEditAllFields}
                        >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="critical">Critical</option>
                        </Select>
                    </FormField>

                    <FormField label="Status" htmlFor="project-status">
                        <Select id="project-status" name="status" value={formData.status} onChange={handleChange}>
                            <option value="planned">Planned</option>
                            <option value="in_progress">In progress</option>
                            <option value="on_hold">On hold</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                        </Select>
                    </FormField>
                </FormGrid>

                <FormGrid cols={3}>
                    <FormField label="Progress" htmlFor="project-progress">
                        <Select id="project-progress" name="progress" value={formData.progress} onChange={handleChange}>
                            {[0, 25, 50, 75, 100].map((p) => (
                                <option key={p} value={p}>
                                    {p}%
                                </option>
                            ))}
                        </Select>
                    </FormField>

                    <FormField label="Start date" htmlFor="project-start">
                        <Input
                            id="project-start"
                            type="date"
                            name="start_date"
                            value={formData.start_date}
                            onChange={handleChange}
                            disabled={!canEditAllFields}
                        />
                    </FormField>

                    <FormField label="End date" htmlFor="project-end">
                        <Input
                            id="project-end"
                            type="date"
                            name="end_date"
                            value={formData.end_date}
                            onChange={handleChange}
                            disabled={!canEditAllFields}
                        />
                    </FormField>
                </FormGrid>

                {/* Attachments */}
                <div className="pt-1 space-y-3">
                    <FormField
                        label="Attachment"
                        htmlFor="project-file"
                        hint="Word, PDF or image. One file per save."
                    >
                        <div className="flex items-center gap-2.5">
                            <label
                                htmlFor="project-file"
                                className={`btn btn-secondary ${!canEditAllFields ? 'opacity-55 pointer-events-none' : ''}`}
                            >
                                <Upload size={14} aria-hidden="true" />
                                Choose file
                            </label>
                            <input
                                id="project-file"
                                type="file"
                                onChange={handleFileChange}
                                className="sr-only"
                                accept=".doc,.docx,.pdf,image/*"
                                disabled={!canEditAllFields}
                            />
                            <span className="text-label text-ink-500 truncate">
                                {selectedFile ? selectedFile.name : 'No file selected'}
                            </span>
                        </div>
                    </FormField>

                    {isEditMode && project.attachments?.length > 0 && (
                        <div>
                            <p className="form-label">Project files</p>
                            <ul className="border border-[var(--border-color)] rounded-md divide-y divide-[var(--divider-color)]">
                                {project.attachments.map((file) => (
                                    <li key={file.id} className="flex items-center gap-2 px-3 py-2">
                                        <Paperclip size={13} className="text-ink-400 shrink-0" aria-hidden="true" />
                                        <a
                                            href={fileUrl(file.file_path)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-base text-ink-700 hover:text-primary-600 truncate transition-colors duration-150"
                                        >
                                            {file.file_name}
                                        </a>
                                        <button
                                            type="button"
                                            onClick={() => handleDeleteFile(file.id)}
                                            aria-label={`Delete ${file.file_name}`}
                                            className="ml-auto p-1 rounded text-ink-400 hover:text-danger hover:bg-danger-subtle transition-colors duration-150"
                                        >
                                            <Trash2 size={14} aria-hidden="true" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </form>
        </Modal>
    );
};

export default ProjectModal;
